import clsx from 'clsx';
import CharityIcon from 'components/assets/CharityIcon.svg';
import VenmoIcon from 'components/assets/VenmoIcon.svg';
import A from 'components/atoms/A/A';
import Heading from 'components/atoms/Heading/Heading';
import React from 'react';
import { graphql, useFragment } from 'react-relay';
import {
  RedemptionTypeKey,
  RedemptionTypesButtons_redemptionTypes$data,
  RedemptionTypesButtons_redemptionTypes$key,
} from './__generated__/RedemptionTypesButtons_redemptionTypes.graphql';
import PayPalIcon from './assets/paypal_logo.svg';
import classes from './RedemptionTypesButtons.module.scss';

export type RedemptionTypeData = {
  Icon?: React.ElementType;
  name: string;
};

export const REDEMPTIONS_PROVIDER_DATA: Record<RedemptionTypeKey, RedemptionTypeData> = {
  PAYPAL: {
    Icon: PayPalIcon,
    name: 'PayPal',
  },
  VENMO: {
    Icon: VenmoIcon,
    name: 'Venmo',
  },
  GIFT_CARD: {
    name: 'E-Gift Cards',
  },
  CHARITY: {
    Icon: CharityIcon,
    name: 'Charity',
  },
  MOBILE: {
    name: 'Mobile-Top Up',
  },
};

const RedemptionTypeButtonContent: React.FC<RedemptionTypeData> = ({ Icon, name }) => (
  <>{Icon ? <Icon className={classes.image} /> : <Heading level={3}>{name}</Heading>}</>
);

const fragmentSpec = graphql`
  fragment RedemptionTypesButtons_redemptionTypes on RedemptionTypes
  @refetchable(queryName: "RedemptionTypesRefetchQuery") {
    types {
      name
      status
    }
  }
`;

export interface Props {
  redemptionTypesRef: RedemptionTypesButtons_redemptionTypes$key;
  className?: string;
  onRedemptionTypeSelect?: (type: RedemptionType) => void;
}

export type RedemptionType = RedemptionTypesButtons_redemptionTypes$data['types'][number];

export default function RedemptionTypesButtons({
  redemptionTypesRef,
  className,
  onRedemptionTypeSelect,
}: Props) {
  const providers: RedemptionTypesButtons_redemptionTypes$data = useFragment(
    fragmentSpec,
    redemptionTypesRef,
  );

  const sortedRedemptionTypes = [...providers.types].sort((a, b) =>
    a.status === 'OFFLINE' && b.status !== 'OFFLINE' ? 1 : 0,
  );

  function handleTypeButtonClick(type: RedemptionType, _e: React.MouseEvent<HTMLAnchorElement>) {
    onRedemptionTypeSelect?.(type);
  }

  return (
    <div className={clsx(classes.cashoutLinks, className)}>
      {sortedRedemptionTypes.map((type: RedemptionType) => (
        <A
          href={'#'}
          key={type.name}
          className={clsx(
            classes.cashoutLink,
            type.status === 'OFFLINE' && classes.offline,
            classes['type-' + type.name.toLowerCase()],
          )}
          onClick={handleTypeButtonClick.bind(null, type)}
        >
          <RedemptionTypeButtonContent
            Icon={REDEMPTIONS_PROVIDER_DATA[type.name].Icon}
            name={REDEMPTIONS_PROVIDER_DATA[type.name].name}
          />
        </A>
      ))}
    </div>
  );
}
